import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Lands here after Supabase completes a Google sign-in (or any other OAuth /
// magic-link / email-confirmation flow). PKCE puts ?code=… in the URL; we
// exchange it explicitly rather than relying on auto-detect, since the
// auto-exchange has a known timing race with onAuthStateChange.

const FALLBACK_TIMEOUT_MS = 10_000;

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>('checking');

  useEffect(() => {
    if (handledRef.current) return;

    const goToDashboard = (reason: string) => {
      if (handledRef.current) return;
      handledRef.current = true;
      console.log(`[auth-callback] success via ${reason}; navigating to /dashboard`);
      navigate('/dashboard', { replace: true });
    };

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

      // Surface explicit errors from the provider/Supabase up front.
      const errParam =
        params.get('error_description') ||
        params.get('error') ||
        hash.get('error_description') ||
        hash.get('error');
      if (errParam) {
        console.error('[auth-callback] provider error:', errParam);
        setError(decodeURIComponent(errParam));
        return;
      }

      // 1. PKCE code present? Exchange it explicitly.
      const code = params.get('code');
      if (code) {
        setStage('exchanging code');
        console.log('[auth-callback] exchanging code for session');
        const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
        if (error) {
          console.error('[auth-callback] exchangeCodeForSession failed:', error);
          setError(error.message);
          return;
        }
        if (data.session) {
          goToDashboard('exchangeCodeForSession');
          return;
        }
      }

      // 2. Implicit-flow access_token in the hash? supabase-js auto-detects;
      //    we just need to wait for the SIGNED_IN event.
      // 3. Already-established session (e.g. user navigated here manually
      //    while signed in)? Fast-path home.
      setStage('checking session');
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        goToDashboard('existing session');
        return;
      }

      // 4. Wait for the SIGNED_IN event as a fallback for the implicit flow
      //    or any timing edge cases.
      setStage('waiting for sign-in event');
      console.log('[auth-callback] waiting for SIGNED_IN event');
    };

    const subscriptionHandle = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[auth-callback] auth state event:', event, !!session);
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        goToDashboard(`event=${event}`);
      }
    });

    run().catch((e) => {
      console.error('[auth-callback] unexpected error:', e);
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    });

    const timer = setTimeout(() => {
      if (handledRef.current) return;
      console.warn('[auth-callback] fallback timeout reached without session');
      setError("We couldn't complete sign-in. Please try again.");
    }, FALLBACK_TIMEOUT_MS);

    return () => {
      subscriptionHandle.data.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md space-y-3">
        {error ? (
          <>
            <h1 className="text-lg font-semibold">Sign-in didn't complete</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              className="text-sm text-primary hover:underline underline-offset-4"
            >
              Try signing in again
            </button>
          </>
        ) : (
          <>
            <div className="w-6 h-6 rounded-full border-2 border-foreground/20 border-t-foreground animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">Finishing sign-in… ({stage})</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
